import React from 'react';
import { Modal, Table, Tag, Timeline, Alert } from 'antd';
import {
    ExclamationCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    CopyOutlined,
    SwapOutlined,
    EyeInvisibleOutlined,
    DisconnectOutlined,
    ClockCircleOutlined,
    ThunderboltOutlined,
    HourglassOutlined,
    CameraOutlined,
    VideoCameraOutlined,
    FullscreenExitOutlined,
    StopOutlined,
} from '@ant-design/icons';
import type { AntiCheatReport, AntiCheatEvent } from '../services/antiCheat';

interface AntiCheatModalProps {
    visible: boolean;
    onClose: () => void;
    report: AntiCheatReport | null;
    loading?: boolean;
}

const AntiCheatModal: React.FC<AntiCheatModalProps> = ({
    visible,
    onClose,
    report,
    loading = false
}) => {
    if (!report) return null;

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical': return '#ff4d4f';
            case 'high': return '#ff7a00';
            case 'medium': return '#faad14';
            case 'low': return '#52c41a';
            default: return '#d9d9d9';
        }
    };

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'critical':
            case 'high':
                return <ExclamationCircleOutlined style={{ color: getSeverityColor(severity) }} />;
            case 'medium':
                return <WarningOutlined style={{ color: getSeverityColor(severity) }} />;
            case 'low':
            default:
                return <InfoCircleOutlined style={{ color: getSeverityColor(severity) }} />;
        }
    };

    const getEventTypeIcon = (eventType: string) => {
        switch (eventType) {
            case 'PASTE_ATTEMPT':
            case 'COPY_ATTEMPT':
            case 'CUT_ATTEMPT':
                return <CopyOutlined style={{ color: '#666' }} />;
            case 'TAB_SWITCH':
            case 'WINDOW_BLUR':
                return <SwapOutlined style={{ color: '#666' }} />;
            case 'DRAG_DROP_ATTEMPT':
                return <EyeInvisibleOutlined style={{ color: '#666' }} />;
            case 'OFFLINE':
                return <DisconnectOutlined style={{ color: '#666' }} />;
            case 'IDLE_TIMEOUT':
                return <ClockCircleOutlined style={{ color: '#666' }} />;
            case 'FAST_ANSWER':
                return <ThunderboltOutlined style={{ color: '#666' }} />;
            case 'SLOW_ANSWER':
                return <HourglassOutlined style={{ color: '#666' }} />;
            case 'SCREENSHOT_ATTEMPT':
                return <CameraOutlined style={{ color: '#ff7a00' }} />;
            case 'SCREEN_CAPTURE_ATTEMPT':
                return <VideoCameraOutlined style={{ color: '#ff4d4f' }} />;
            case 'FULLSCREEN_EXIT':
                return <FullscreenExitOutlined style={{ color: '#faad14' }} />;
            case 'RIGHT_CLICK_ATTEMPT':
                return <StopOutlined style={{ color: '#666' }} />;
            default:
                return <InfoCircleOutlined style={{ color: '#666' }} />;
        }
    };

    const getRiskLevelAlert = () => {
        const { risk_level } = report;
        const props: any = {
            showIcon: true,
            style: { marginBottom: 16 }
        };

        switch (risk_level) {
            case 'critical':
                return (
                    <Alert
                        {...props}
                        type="error"
                        message="Critical Risk Level"
                        description="Multiple high-severity violations detected. This assessment result should be reviewed carefully."
                    />
                );
            case 'high':
                return (
                    <Alert
                        {...props}
                        type="warning"
                        message="High Risk Level"
                        description="Several suspicious activities detected during the assessment."
                    />
                );
            case 'medium':
                return (
                    <Alert
                        {...props}
                        type="info"
                        message="Medium Risk Level"
                        description="Some suspicious activities detected, but overall acceptable."
                    />
                );
            case 'low':
            default:
                return (
                    <Alert
                        {...props}
                        type="success"
                        message="Low Risk Level"
                        description="Minimal or no suspicious activities detected during the assessment."
                    />
                );
        }
    };

    // Table columns for event summary
    const summaryColumns = [
        {
            title: 'Event Type',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {getEventTypeIcon(Object.keys(report.event_summary).find(key =>
                        report.event_summary[key].name === text) || '')}
                    {getSeverityIcon(record.severity)}
                    <span style={{ fontWeight: 500 }}>{text}</span>
                </div>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Count',
            dataIndex: 'count',
            key: 'count',
            width: 80,
            render: (count: number) => (
                <span style={{ fontWeight: 600, fontSize: 16 }}>{count}</span>
            ),
        },
        {
            title: 'Severity',
            dataIndex: 'severity',
            key: 'severity',
            width: 100,
            render: (severity: string) => (
                <Tag color={getSeverityColor(severity)} style={{ fontWeight: 500 }}>
                    {severity.toUpperCase()}
                </Tag>
            ),
        },
    ];

    // Convert event summary to table data
    const summaryData = Object.entries(report.event_summary).map(([eventType, summary]) => ({
        key: eventType,
        ...summary,
    }));

    // Format flagged events for timeline
    const timelineItems = report.flagged_events.map((event: AntiCheatEvent, index: number) => ({
        color: getSeverityColor(event.severity),
        children: (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{event.name}</span>
                    <Tag color={getSeverityColor(event.severity)}>
                        {event.severity.toUpperCase()}
                    </Tag>
                </div>
                <div style={{ color: '#666', fontSize: 14 }}>{event.description}</div>
                <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                    {new Date(event.timestamp).toLocaleString()}
                </div>
            </div>
        ),
    }));

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                    Anti-Cheat Report
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            style={{ top: 80 }}
            zIndex={1050}
        >
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Header Info */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                        {report.candidate_name}
                    </div>
                    <div style={{ color: '#666', fontSize: 14 }}>
                        Assessment: {report.test_name}
                    </div>
                </div>

                {/* Risk Level Alert */}
                {getRiskLevelAlert()}

                {/* Summary Stats */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 16,
                    marginBottom: 24,
                    padding: 16,
                    background: '#fafafa',
                    borderRadius: 8
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>
                            {report.total_events}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>Total Events</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#ff4d4f' }}>
                            {report.flagged_events_count}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>Flagged Events</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: getSeverityColor(report.risk_level),
                            textTransform: 'capitalize'
                        }}>
                            {report.risk_level}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>Risk Level</div>
                    </div>
                </div>

                {/* Duplicate Assessments Section */}
                {report.has_duplicates && (
                    <div style={{ marginBottom: 24 }}>
                        <Alert
                            type="warning"
                            message="Recent Assessment Activity"
                            description={
                                <div style={{ marginTop: 8 }}>
                                    <p style={{ marginBottom: 12, fontSize: 14 }}>
                                        This candidate has taken the same assessment recently from other sources:
                                    </p>
                                    {report.duplicate_assessments.map((duplicate, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                background: '#fff7e6',
                                                border: '1px solid #ffd591',
                                                borderRadius: 6,
                                                padding: 12,
                                                marginBottom: 8,
                                                fontSize: 13
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                                {duplicate.status} {duplicate.days_ago} days ago
                                            </div>
                                            <div style={{ color: '#666' }}>
                                                Source: {duplicate.inviter}
                                            </div>
                                            {duplicate.status === 'Completed' && (
                                                <div style={{ color: '#d46b08', fontSize: 12, marginTop: 4 }}>
                                                    ⚠️ Candidate may have prior knowledge of questions
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            }
                        />
                    </div>
                )}

                {/* Event Summary Table */}
                {summaryData.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                            Event Summary
                        </h3>
                        <Table
                            columns={summaryColumns}
                            dataSource={summaryData}
                            pagination={false}
                            size="small"
                            bordered
                        />
                    </div>
                )}

                {/* Flagged Events Timeline */}
                {report.flagged_events.length > 0 && (
                    <div>
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                            Recent Flagged Events
                        </h3>
                        <Timeline items={timelineItems} />
                    </div>
                )}

                {/* No violations message */}
                {!report.has_violations && (
                    <Alert
                        type="success"
                        message="No Violations Detected"
                        description="This candidate completed the assessment without triggering any anti-cheat mechanisms."
                        showIcon
                    />
                )}
            </div>
        </Modal>
    );
};

export default AntiCheatModal;